// pages/WebsiteLinks/ListWebsiteErrors.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import DataTable from 'react-data-table-component';

import {
    Box,
    Typography,
    Alert,
    TextField,
    IconButton,
    CircularProgress,
    Button,
    Tooltip,
    useTheme,
    Card,
    CardContent,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip
} from "@mui/material";
import {
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    HourglassEmpty as HourglassEmptyIcon,
    CloudUpload as ScrapeIcon,
} from "@mui/icons-material";


// Reusable ScrapeStatusModal using MUI Dialog
const ScrapeStatusModal = ({ isOpen, onRequestClose, scrapeResults }) => {
    return (
        <Dialog open={isOpen} onClose={onRequestClose} fullWidth maxWidth="md">
            <DialogTitle>Re-Scraping Status</DialogTitle>
            <DialogContent dividers>
                <List>
                    {scrapeResults.map((result, index) => (
                        <ListItem key={index} divider>
                            <ListItemIcon>
                                {result.status === 'success' && <CheckCircleIcon color="success" />}
                                {result.status === 'error' && <ErrorIcon color="error" />}
                                {result.status === 'pending' && <HourglassEmptyIcon color="action" />}
                            </ListItemIcon>
                            <ListItemText
                                primary={result.website_link}
                                secondary={result.error || result.status}
                                primaryTypographyProps={{ style: { wordBreak: 'break-all' } }}
                            />
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onRequestClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

const ExpandedRow = ({ data }) => {
    return (
        <Box p={2} sx={{ backgroundColor: 'action.hover', lineHeight: 1.8 }}>
            <Typography variant="body2"><strong>Website Link:</strong> {data.website_link}</Typography>
            <Typography variant="body2"><strong>Error Message:</strong> {data.scrape_error_message}</Typography>
        </Box>
    );
};


export default function ListWebsiteErrors({ refresh, setRefresh, chatbotId, permission }) {
    const theme = useTheme();
    const [documents, setDocuments] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectionError, setSelectionError] = useState(null);
    const [fetching, setFetching] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [scrapeModalIsOpen, setScrapeModalIsOpen] = useState(false);
    const [scrapeResults, setScrapeResults] = useState([]);

    const fetchDocuments = async (page = 1, limit = perPage, search = "") => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/get-unscrape-error-website-links`, {
                params: { page, limit, search, ...(chatbotId ? { chatbot_id: chatbotId } : {}) }
            });
            const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
            setDocuments(responseData.data);
            setTotalRows(responseData.total || 0);
            setCurrentPage(page);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = 'Website Errors | KlonIT Web Application';
        fetchDocuments(currentPage, perPage, searchText);
    }, [refresh, searchText]);

    const getFileType = (websiteLink) => {
        const lowerCaseLink = websiteLink.toLowerCase();
        if (lowerCaseLink.endsWith('.pdf')) return "PDF";
        if (lowerCaseLink.endsWith('.doc') || lowerCaseLink.endsWith('.docx')) return "Document";
        if (lowerCaseLink.endsWith('.png') || lowerCaseLink.endsWith('.jpg') || lowerCaseLink.endsWith('.jpeg')) return "Image";
        if (lowerCaseLink.endsWith('.csv') || lowerCaseLink.endsWith('.xlsx') || lowerCaseLink.endsWith('.xls')) return "Structure Document";
        if (lowerCaseLink.startsWith('http')) return "URL";
        return "Other";
    };

    const getFileTypeColor = (fileType) => {
        switch (fileType) {
            case "PDF": return "error";
            case "URL": return "success";
            case "Document": return "primary";
            case "Image": return "secondary";
            case "Structure Document": return "warning";
            default: return "default";
        }
    };
    
    const delay = (ms) => new Promise(res => setTimeout(res, ms));

    const handleScrapeDataBTN = async () => {
        if (selectedDocuments.length === 0) {
            setError("No documents selected for scraping.");
            return;
        }

        setFetching(true);
        setError(null);
        setScrapeModalIsOpen(true);
        const params = chatbotId ? { chatbot_id: chatbotId } : {};
        let currentScrapeResults = selectedDocuments.map(doc => ({ website_link: doc.website_link, status: 'pending', error: null }));
        setScrapeResults(currentScrapeResults);

        for (let i = 0; i < selectedDocuments.length; i++) {
            const doc = selectedDocuments[i];
            try {
                const response = await axiosInstance.post(`/add-scrape-data`, { data: [[doc.id], [doc.website_link]] }, { params });
                const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

                if (responseData?.scrape_data?.[0]?.page_content) {
                    currentScrapeResults[i].status = 'success';
                } else {
                    currentScrapeResults[i].status = 'error';
                    currentScrapeResults[i].error = responseData?.scrape_data?.[0]?.error || 'Scraping failed';
                }
            } catch (error) {
                currentScrapeResults[i].status = 'error';
                currentScrapeResults[i].error = error.message || 'Request failed';
            }
            setScrapeResults([...currentScrapeResults]);
            if (i < selectedDocuments.length - 1) await delay(1000);
        }

        setFetching(false);
        setSelectedDocuments([]);
        setToggleCleared(!toggleCleared);
        if (setRefresh) setRefresh((prev) => !prev);
        
        const failureCount = currentScrapeResults.filter(res => res.status === 'error').length;
        if (failureCount > 0) {
            setError(`${selectedDocuments.length - failureCount} URLs scraped successfully, while ${failureCount} failed.`);
        } else {
            setError("All selected URLs were successfully re-scraped.");
        }
    };

    const handleBulkDelete = async () => {
        if (selectedDocuments.length === 0) return;
        if (!window.confirm(`Are you sure you want to delete ${selectedDocuments.length} selected items?`)) return;

        try {
            const params = chatbotId ? { chatbot_id: chatbotId } : {};
            await Promise.all(selectedDocuments.map(doc => axiosInstance.delete(`/delete-website-link/${doc.id}`, { params })));
            setSelectedDocuments([]);
            setToggleCleared(!toggleCleared);
            setError("Selected links deleted successfully.");
            fetchDocuments(currentPage, perPage, searchText);
        } catch (error) {
            setError(error.message);
        }
    };

    const handleDelete = async (documentId) => {
        if (!window.confirm(`Are you sure you want to delete this item?`)) return;
        try {
            const params = chatbotId ? { chatbot_id: chatbotId } : {};
            await axiosInstance.delete(`/delete-website-link/${documentId}`, { params });
            setError("Link deleted successfully.");
            fetchDocuments(currentPage, perPage, searchText);
        } catch (error) {
            setError(error.message);
        }
    };

    const customDataTableStyles = {
        table: { style: { backgroundColor: theme.palette.background.paper } },
        headRow: { style: { backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9', borderBottom: `1px solid ${theme.palette.divider}` } },
        headCells: { style: { fontWeight: 600, color: theme.palette.text.primary } },
        rows: { style: { backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, '&:hover': { backgroundColor: `${theme.palette.action.hover} !important` } } },
        pagination: { style: { backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, borderTop: `1px solid ${theme.palette.divider}` } },
        expanderRow: { style: { backgroundColor: theme.palette.action.hover } },
    };

    const columns = [
        { name: 'Website Link', selector: row => row.website_link, sortable: true, wrap: true, grow: 2 },
        { name: 'File Type', selector: row => getFileType(row.website_link), sortable: true, width: '100px', cell: row => { const type = getFileType(row.website_link); return <Chip label={type} color={getFileTypeColor(type)} size="small" />; }, hide: 'sm' },
        { name: 'Status', selector: row => row.scrape_status, sortable: true, width: '90px', hide: 'sm' },
        { name: 'Error Message', selector: row => row.scrape_error_message, sortable: true, width: '200px', wrap: true, hide: 'md' },
        { name: 'Date', selector: row => row.created_date, sortable: true, width: '120px', hide: 'md' },
        { name: 'Action', width: '100px', cell: row => (
            <Box>
                <Tooltip title="View Website Link"><IconButton size="small" onClick={() => window.open(row.website_link, '_blank')}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                {permission.canDelete && <Tooltip title="Delete Website Link"><IconButton size="small" color="error" onClick={() => handleDelete(row.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
            </Box>
        )},
    ];

    return (
        <Box>
            <Card>
                <CardContent>
                    <Grid container alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={8}>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                                {permission.canDelete && <Button variant="outlined" color="error" onClick={handleBulkDelete} disabled={selectedDocuments.length === 0 || fetching} startIcon={fetching ? <CircularProgress size={16} /> : <DeleteIcon />} size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Delete ({selectedDocuments.length})</Button>}
                                {permission.canModify && <Button variant="outlined" color="primary" onClick={handleScrapeDataBTN}  disabled={selectedDocuments.length === 0 || fetching} startIcon={fetching ? <CircularProgress size={16} /> : <ScrapeIcon />} size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Re-Scrape ({selectedDocuments.length})</Button>}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}><TextField fullWidth variant="outlined" size="small" placeholder="Search URL, Title..." value={searchText} onChange={(e) => setSearchText(e.target.value)} /></Grid>
                    </Grid>
                    
                    {error && <Alert severity={error.includes("successfully") ? "success" : "warning"} sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
                    {selectionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSelectionError(null)}>{selectionError}</Alert>}
                    
                    {loading ? (
                        <Box display="flex" flexDirection="column" alignItems="center" py={4}><CircularProgress /><Typography mt={2}>Loading Website Errors...</Typography></Box>
                    ) : documents.length === 0 ? (
                        <Box textAlign="center" py={4}><Typography variant="h6" color="text.secondary">No scraping errors found.</Typography></Box>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={documents}
                            pagination 
                            paginationServer 
                            paginationTotalRows={totalRows} 
                            paginationPerPage={perPage}
                            onChangePage={(page) => fetchDocuments(page, perPage, searchText)}
                            onChangeRowsPerPage={(newPerPage, page) => { setPerPage(newPerPage); fetchDocuments(page, newPerPage, searchText); }}
                            selectableRows
                            onSelectedRowsChange={({ selectedRows }) => setSelectedDocuments(selectedRows)}
                            clearSelectedRows={toggleCleared}
                            expandableRows
                            expandableRowsComponent={ExpandedRow}
                            customStyles={customDataTableStyles}
                            theme={theme.palette.mode === 'dark' ? 'dark' : 'default'}
                        />
                    )}
                </CardContent>
            </Card>
            <ScrapeStatusModal isOpen={scrapeModalIsOpen} onRequestClose={() => setScrapeModalIsOpen(false)} scrapeResults={scrapeResults} />
        </Box>
    );
}